import React, { useEffect, useState } from 'react';
import AdminSetupPage from './AdminSetupPage';
import AdminArchivePage from './AdminArchivePage';
import AdminPollerPage from './AdminPollerPage';
import AdminBrandingPage from './AdminBrandingPage';
import AdminPayoutPage from './AdminPayoutPage';
import AdminPaymentPage from './AdminPaymentPage';
import AdminResultsBracket from './AdminResultsBracket';
import AdminBracketEditPage from './AdminBracketEditPage';
import { useApi } from '../hooks/useApi';
import { usePrintBracket } from '../hooks/usePrintBracket';
import ResultsBracketPrintView from '../components/ResultsBracketPrintView';
import { invalidateDeadlinesCache } from '../hooks/useDeadlines';
import BracketPrintView from '../components/BracketPrintView';


function buildEliminatedSet(games, slugToId = {}) {
  const eliminated = new Set();
  const makeSlug = str => (str || '').toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  (games || []).forEach(g => {
    if ((g.status === 'final' || g.status === 'Final') && g.winner) {
      const loser = g.homeTeam?.id === g.winner ? g.awayTeam : g.homeTeam;
      if (loser?.id && loser.id !== 'tbd') {
        const loserId = String(loser.id);
        eliminated.add(loserId);
        if (isNaN(loserId)) {
          const numId = slugToId[loserId] || slugToId[makeSlug(loser.name || '')] || loser.espnId;
          if (numId) eliminated.add(String(numId));
        }
        if (loser.espnId) eliminated.add(String(loser.espnId));
      }
    }
  });
  return eliminated;
}

function BracketsTable({ brackets, bracketSort, setBracketSort, teamsById, setEditingBracket, handleDeleteBracket, onPrint, api, msg, loadBrackets, eliminatedIds, gamesById, paidOverrides, onTogglePaid, espnNameCache = {} }) {
  const [adjustments, setAdjustments] = React.useState({});
  const SortBtn = ({ col, label }) => {
    const active = bracketSort.col === col;
    const dir = active ? bracketSort.dir : null;
    const next = !active ? 'asc' : dir === 'asc' ? 'desc' : dir === 'desc' ? null : 'asc';
    return (
      <button onClick={() => setBracketSort({ col: next ? col : null, dir: next })}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: active ? 'var(--blue)' : 'var(--gray)', fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '1px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}>
        {label}
        <span style={{ fontSize: '0.7rem' }}>{dir === 'asc' ? '↑' : dir === 'desc' ? '↓' : '↕'}</span>
      </button>
    );
  };

  const sorted = [...brackets].sort((a, b) => {
    if (!bracketSort.col || !bracketSort.dir) return 0;
    const dir = bracketSort.dir === 'asc' ? 1 : -1;
    const sleeperA = teamsById[a.sleeperTeamId]?.name || espnNameCache[a.sleeperTeamId] || a.sleeperTeamId || '';
    const sleeperB = teamsById[b.sleeperTeamId]?.name || espnNameCache[b.sleeperTeamId] || b.sleeperTeamId || '';
    const champA = teamsById[a.picks?.['final-1']]?.name || '';
    const champB = teamsById[b.picks?.['final-1']]?.name || '';
    switch (bracketSort.col) {
      case 'name': return dir * (a.name || '').localeCompare(b.name || '');
      case 'user': return dir * (a.userName || '').localeCompare(b.userName || '');
      case 'sleeper': return dir * sleeperA.localeCompare(sleeperB);
      case 'champion': return dir * champA.localeCompare(champB);
      case 'score': return dir * ((a.score || 0) - (b.score || 0));
      case 'date': return dir * (a.submittedAt || '').localeCompare(b.submittedAt || '');
      default: return 0;
    }
  });

  return (
    <table className="leaderboard-table">
      <thead>
        <tr>
          <th><SortBtn col="name" label="Bracket" /></th>
          <th><SortBtn col="user" label="User" /></th>
          <th>Email</th>
          <th><SortBtn col="sleeper" label="Sleeper" /></th>
          <th><SortBtn col="champion" label="Champion" /></th>
          <th><SortBtn col="score" label="Score" /></th>
          <th><SortBtn col="date" label="Submitted" /></th>
          <th style={{ textAlign: 'center', width: 40 }}>Paid</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map(b => {
          const sleeperName = teamsById[b.sleeperTeamId]?.name || espnNameCache[b.sleeperTeamId] || b.sleeperTeamId || '—';
          const rawChampId = b.picks?.['final-1'];
          const resolveChamp = (id) => {
            if (!id) return '—';
            if (teamsById[id]) return teamsById[id].name;
            const pg = gamesById?.[id];
            if (pg?.winner) return teamsById[pg.winner]?.name || (pg.homeTeam?.id === pg.winner ? pg.homeTeam?.name : pg.awayTeam?.name) || id;
            if (pg) return pg.shortName || id;
            return id;
          };
          const champId = rawChampId;
          const resolvedChampId = gamesById?.[rawChampId]?.winner || rawChampId;
          const champName = resolveChamp(rawChampId);
          const adj = b.scoreAdjustment || 0;
          return (
            <tr key={b.id}>
              <td style={{ whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}><strong>{b.name}</strong></td>
              <td style={{ whiteSpace: 'nowrap' }}>{b.userName}</td>
              <td style={{ fontSize: '0.85rem', color: 'var(--gray)', whiteSpace: 'nowrap' }}>{b.userEmail}</td>
              <td>
                <span className="badge badge-gold" style={{
                  textDecoration: (eliminatedIds?.has(String(b.sleeperTeamId)) || eliminatedIds?.has(b.sleeperTeamId)) ? 'line-through' : 'none',
                  opacity: (eliminatedIds?.has(String(b.sleeperTeamId)) || eliminatedIds?.has(b.sleeperTeamId)) ? 0.6 : 1,
                  maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle',
                }} title={sleeperName}>★ {sleeperName}</span>
                {(eliminatedIds?.has(String(b.sleeperTeamId)) || eliminatedIds?.has(b.sleeperTeamId)) && <span style={{ marginLeft: 4, fontSize: '0.75rem' }}>💀</span>}
              </td>
              <td style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700,
                color: (eliminatedIds?.has(String(resolvedChampId)) || eliminatedIds?.has(resolvedChampId)) ? 'var(--red)' : 'var(--cream)',
                textDecoration: (eliminatedIds?.has(String(resolvedChampId)) || eliminatedIds?.has(resolvedChampId)) ? 'line-through' : 'none',
                whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                🏆 {champName}
                {(eliminatedIds?.has(String(resolvedChampId)) || eliminatedIds?.has(resolvedChampId)) && <span style={{ marginLeft: 4, fontSize: '0.75rem' }}>💀</span>}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span className="score-value">{b.score || 0}</span>
                  {adj !== 0 && <span style={{ fontSize: '0.72rem', color: adj > 0 ? 'var(--green)' : 'var(--red)', fontFamily: 'var(--font-condensed)' }}>({adj > 0 ? '+' : ''}{adj})</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <input
                    type="number"
                    title="Score adjustment (+/-)"
                    placeholder="±adj"
                    value={adjustments[b.id] !== undefined ? adjustments[b.id] : (adj || '')}
                    style={{ width: 52, padding: '2px 4px', fontSize: '0.78rem', background: 'var(--navy-light)', border: '1px solid var(--slate)', borderRadius: 4, color: 'var(--cream)', fontFamily: 'var(--font-condensed)' }}
                    onChange={e => setAdjustments(a => ({ ...a, [b.id]: e.target.value }))}
                  />
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                    onClick={async () => {
                      const val = Number(adjustments[b.id]) || 0;
                      try {
                        await api.adminUpdateBracketPicks(b.id, { scoreAdjustment: val, note: `Score adjustment: ${val > 0 ? '+' : ''}${val}` });
                        msg(`✓ Adjustment saved for ${b.name}`);
                        setAdjustments(a => { const n = {...a}; delete n[b.id]; return n; });
                        loadBrackets();
                      } catch (err) { msg(err.message, true); }
                    }}
                    disabled={adjustments[b.id] === undefined}
                  >Set</button>
                </div>
              </td>
              <td style={{ fontSize: '0.8rem', color: 'var(--gray)' }}>
                {b.submittedAt ? new Date(b.submittedAt).toLocaleDateString() : '—'}
              </td>
              <td style={{ textAlign: 'center' }}>
                {(() => {
                  const currentPaid = paidOverrides[b.id] !== undefined ? paidOverrides[b.id] : !!b.paid;
                  const isChanged = paidOverrides[b.id] !== undefined;
                  return (
                    <button
                      title={currentPaid ? 'Paid — click to toggle unpaid' : 'Unpaid — click to toggle paid'}
                      onClick={() => onTogglePaid(b.id, currentPaid)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', position: 'relative', padding: '2px 4px' }}>
                      <span style={{ color: currentPaid ? '#27ae60' : '#e74c3c', textDecoration: currentPaid ? 'none' : 'line-through' }}>$</span>
                      {isChanged && <span style={{ position: 'absolute', top: -2, right: -2, color: '#4b8bbe', fontSize: '0.55rem', fontWeight: 900 }}>★</span>}
                    </button>
                  );
                })()}
              </td>
              <td>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: '0.85rem', padding: '4px 8px' }} title='Edit Picks' onClick={() => setEditingBracket(b.id)}>&#x270E;</button>
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: '0.72rem', padding: '4px 8px' }} title="Print / Save PDF" onClick={() => onPrint && onPrint(b.id, b)}>🖨</button>
                  <button className="btn btn-danger btn-sm" style={{ fontSize: '0.85rem', padding: '4px 8px' }} title='Delete' onClick={() => handleDeleteBracket(b.id, b.name)}>&#x1F5D1;</button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function InlineGameEdit({ game, gameEdit, setGameEdit, onSave, onCancel, onPrev, onNext, hasPrev, hasNext }) {
  const isTbd = t => !t || t.id === 'tbd' || t.id?.includes('winner');
  const ht = game.homeTeam, at = game.awayTeam;
  const title = (isTbd(ht) && isTbd(at)) ? (game.shortName || game.id)
    : `(${ht?.seed||'?'}) ${ht?.name||'?'} vs (${at?.seed||'?'}) ${at?.name||'?'}`;

  // Keyboard shortcuts: Enter=save, Escape=cancel
  React.useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) onSave();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onSave, onCancel]);

  return (
    <tr>
      <td colSpan={7} style={{ padding: 0 }}>
        <div style={{
          background: 'rgba(75,139,190,0.08)', border: '2px solid var(--blue)',
          borderRadius: 8, padding: '16px 20px', margin: '2px 0',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem', color: 'var(--blue)', letterSpacing: '1px' }}>
              ✎ {title}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={onPrev} disabled={!hasPrev} title="Previous game (←)">← Prev</button>
              <button className="btn btn-secondary btn-sm" onClick={onNext} disabled={!hasNext} title="Next game (→)">Next →</button>
              <button className="btn btn-secondary btn-sm" onClick={onCancel} title="Cancel (Esc)">✕ Cancel</button>
            </div>
          </div>

          {/* Fields */}
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 2fr auto', gap: 12, alignItems: 'end' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Status</label>
              <select className="form-select" style={{ width: 110 }} value={gameEdit.status || ''} onChange={e => setGameEdit(x => ({ ...x, status: e.target.value }))}>
                <option value="scheduled">Scheduled</option>
                <option value="live">Live</option>
                <option value="final">Final</option>
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>{ht?.name || 'Home'} Score</label>
              <input className="form-input" type="number" value={gameEdit.homeScore ?? ''} onChange={e => setGameEdit(x => ({ ...x, homeScore: e.target.value }))} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>{at?.name || 'Away'} Score</label>
              <input className="form-input" type="number" value={gameEdit.awayScore ?? ''} onChange={e => setGameEdit(x => ({ ...x, awayScore: e.target.value }))} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Winner</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[ht, at].filter(t => t && t.id !== 'tbd').map(t => (
                  <button key={t.id} onClick={() => setGameEdit(x => ({ ...x, winner: t.id, status: 'final' }))}
                    style={{ padding: '6px 14px', borderRadius: 6, cursor: 'pointer', border: '2px solid',
                      fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.9rem',
                      borderColor: gameEdit.winner === t.id ? 'var(--green)' : 'var(--slate)',
                      background: gameEdit.winner === t.id ? 'rgba(39,174,96,0.15)' : 'var(--navy-light)',
                      color: gameEdit.winner === t.id ? 'var(--green)' : 'var(--cream)' }}>
                    ({t.seed}) {t.name}
                  </button>
                ))}
                <button onClick={() => setGameEdit(x => ({ ...x, winner: '' }))}
                  style={{ padding: '6px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--slate)',
                    background: !gameEdit.winner ? 'rgba(255,107,0,0.1)' : 'transparent',
                    color: 'var(--gray)', fontFamily: 'var(--font-condensed)', fontSize: '0.85rem' }}>
                  No winner
                </button>
              </div>
            </div>
            <button className="btn btn-primary" onClick={onSave} title="Save (Ctrl+Enter)" style={{ whiteSpace: 'nowrap' }}>
              💾 Save
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function AdminPage() {
  const api = useApi();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [brackets, setBrackets] = useState([]);
  const [games, setGames] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [wiring, setWiring] = useState(null);
  const [backupMeta, setBackupMeta] = useState(null);
  const [backupMsg, setBackupMsg] = useState('');
  const [backupBusy, setBackupBusy] = useState(false);
  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    fetch(apiBase + '/wiring').then(r => r.ok ? r.json() : null).then(d => { if(d) setWiring(d); }).catch(()=>{});
    api.adminGetBackup().then(d => setBackupMeta(d)).catch(() => {});
  }, []);
  const [error, setError] = useState('');
  const [deadlines, setDeadlines] = useState({ playInDeadline: '', regularDeadline: '' });
  const [scoringConfig, setScoringConfig] = useState(null);
  const [playInRoundName, setPlayInRoundName] = useState('First Four');
  const [scoringMsg, setScoringMsg] = useState('');
  const [editingGame, setEditingGame] = useState(null);
  const [teamsById, setTeamsById] = useState({});
  const [espnNameCache, setEspnNameCache] = useState({});
  const [espnSlugToId, setEspnSlugToId] = useState({});
  const [setPasswordUser, setSetPasswordUser] = useState(null); // { uid, email, displayName }
  const [newPassword, setNewPassword] = useState('');
  const [bracketSort, setBracketSort] = useState({ col: null, dir: null });
  const [userSort, setUserSort] = useState({ col: 'displayName', dir: 'asc' });
  const [paidOverrides, setPaidOverrides] = useState({}); // bracketId -> true/false
  const [announcement, setAnnouncement] = useState('');
  const [announcementDraft, setAnnouncementDraft] = useState('');
  const [announcementSaving, setAnnouncementSaving] = useState(false);
  const [gameEdit, setGameEdit] = useState({});
  const [gameRoundFilter, setGameRoundFilter] = useState('');
  const [editingBracket, setEditingBracket] = useState(null);
  const [bracketResultsMode, setBracketResultsMode] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const { printData, printLoading, printError, openPrint, closePrint } = usePrintBracket();
  const [showResultsBracket, setShowResultsBracket] = useState(false);
  const [resultsBracketData, setResultsBracketData] = useState(null);

  // Load ESPN teams cache once on mount for name resolution
  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || '/api';
    fetch(`${API}/espn-teams`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.teams) {
          const cache = {};
          const slugMap = {};
          const makeSlug = str => (str || '').toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
          Object.entries(data.teams).forEach(([id, t]) => {
            cache[id] = t.name;
            if (t.slug) slugMap[t.slug] = id;
            slugMap[makeSlug(t.name)] = id;
            if (t.shortName) slugMap[makeSlug(t.shortName)] = id;
            if (t.abbrev) slugMap[t.abbrev.toLowerCase()] = id;
          });
          setEspnNameCache(cache);
          setEspnSlugToId(slugMap);
        }
      })
      .catch(() => {});
  }, []);

  async function loadData(tab) {
    setLoading(true);
    setError('');
    try {
      // Load teams for name resolution (used by brackets tab)
      if (Object.keys(teamsById).length === 0) {
        Promise.all([
          api.getTeams().catch(() => ({ teams: [] })),
          api.adminGetGames().catch(() => []),
        ]).then(([t, games]) => {
          const tById = {};
          // Index by doc ID and espnId
          (t?.teams || []).forEach(tm => {
            tById[tm.id] = tm;
            if (tm.espnId) tById[String(tm.espnId)] = tm;
          });
          // Also scan all game team objects — covers cases where team doc ID is slug
          // but game objects have the ESPN numeric ID on the team
          (Array.isArray(games) ? games : []).forEach(g => {
            [g.homeTeam, g.awayTeam].forEach(team => {
              if (team && team.id && team.name && team.id !== 'tbd' && !String(team.id).includes('winner')) {
                if (!tById[String(team.id)]) {
                  tById[String(team.id)] = team;
                }
              }
            });
          });
          setTeamsById(tById);
        });
      }
      if (tab === 'overview') {
        const s = await api.adminGetStats();
        setStats(s);
        // Load playInRoundName on initial load
        api.get('/admin/app-config').then(cfg => { if (cfg?.playInRoundName) setPlayInRoundName(cfg.playInRoundName); }).catch(() => {});
        // Load announcement
        const apiBase = process.env.REACT_APP_API_URL || '/api';
        fetch(`${apiBase}/announcement`).then(r => r.json()).then(d => {
          setAnnouncement(d.text || '');
          setAnnouncementDraft(d.text || '');
        }).catch(() => {});
      } else if (tab === 'brackets') {
        setPaidOverrides({});
        const [b, g] = await Promise.all([
          api.adminGetBrackets(),
          games.length === 0 ? api.adminGetGames().catch(() => []) : Promise.resolve(games),
        ]);
        setBrackets(b);
        if (Array.isArray(g) && g.length > 0) setGames(g);
      } else if (tab === 'games') {
        const g = await api.adminGetGames();
        setGames(Array.isArray(g) ? g : []);
      } else if (tab === 'users') {
        const u = await api.adminGetUsers();
        setUsers(u);
      } else if (tab === 'scoring') {
        const sc = await api.get('/admin/scoring').catch(() => null);
        if (sc) setScoringConfig(sc);
        const appCfg = await api.get('/admin/app-config').catch(() => null);
        if (appCfg?.playInRoundName) setPlayInRoundName(appCfg.playInRoundName);
      } else if (tab === 'deadlines') {
        const d = await api.adminGetDeadlines();
        // datetime-local input needs format: "YYYY-MM-DDTHH:mm" in local time
        const toLocal = iso => {
          if (!iso) return '';
          const dt = new Date(iso);
          // Pad to YYYY-MM-DDTHH:mm format
          const pad = n => String(n).padStart(2, '0');
          return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
        };
        setDeadlines({
          playInDeadline:  toLocal(d.playInDeadline),
          regularDeadline: toLocal(d.regularDeadline),
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(activeTab); }, [activeTab]);

  function msg(m, isError = false) {
    if (isError) setError(m);
    else { setActionMsg(m); setTimeout(() => setActionMsg(''), 3000); }
  }

  async function handleRecalculate() {
    try {
      await api.adminRecalculate();
      msg('✓ Scores recalculated!');
      if (activeTab === 'brackets') loadData('brackets');
    } catch (err) { msg(err.message, true); }
  }

  async function handlePoll() {
    try {
      await api.adminPoll();
      msg('✓ ESPN poll triggered!');
    } catch (err) { msg(err.message, true); }
  }

  async function handleDeleteBracket(id, name) {
    if (!window.confirm(`Delete bracket "${name}"?`)) return;
    try {
      await api.adminDeleteBracket(id);
      setBrackets(b => b.filter(x => x.id !== id));
      msg('✓ Bracket deleted');
    } catch (err) { msg(err.message, true); }
  }

  async function handleToggleAdmin(uid, currentStatus, email) {
    if (!window.confirm(`${currentStatus ? 'Remove' : 'Grant'} admin for ${email}?`)) return;
    try {
      await api.adminUpdateUser(uid, { isAdmin: !currentStatus });
      setUsers(u => u.map(x => x.id === uid ? { ...x, isAdmin: !currentStatus } : x));
      msg(`✓ Admin ${!currentStatus ? 'granted' : 'revoked'}`);
    } catch (err) { msg(err.message, true); }
  }

  async function handleSaveDeadlines() {
    try {
      // datetime-local gives us "YYYY-MM-DDTHH:mm" in local time — convert to ISO
      const toISO = localStr => localStr ? new Date(localStr).toISOString() : null;
      await api.adminSetDeadlines({
        playInDeadline:  toISO(deadlines.playInDeadline),
        regularDeadline: toISO(deadlines.regularDeadline),
      });
      invalidateDeadlinesCache(); // force fresh fetch across the app
      msg('✓ Deadlines saved!');
    } catch (err) { msg(err.message, true); }
  }

  const TABS = ['overview', 'brackets', 'games', 'results', 'users', 'deadlines', 'scoring', 'setup', 'archive', 'poller', 'branding', 'payment', 'payout'];

  async function handleSaveGame() {
    if (!editingGame) return;
    try {
      const editGame = games.find(x => x.id === editingGame);
      if (!editGame) return;
      const update = {
        status: gameEdit.winner ? 'final' : gameEdit.status,
        winner: gameEdit.winner || null,
        homeTeam: { ...editGame.homeTeam, score: Number(gameEdit.homeScore) || 0 },
        awayTeam: { ...editGame.awayTeam, score: Number(gameEdit.awayScore) || 0 },
      };
      await api.adminUpdateGame(editingGame, update);
      setSaveStatus('✅ Game updated and scores recalculated.');
      setEditingGame(null);
      const updated = await api.adminGetGames();
      setGames(Array.isArray(updated) ? updated : []);
    } catch (err) {
      setSaveStatus('Error: ' + err.message);
    }
  }

  async function handleDeleteUser(uid, email) {
    if (!window.confirm(`Delete user ${email}? This removes them from Auth and Firestore. Their brackets will remain.`)) return;
    try {
      await api.adminDeleteUser(uid);
      setUsers(u => u.filter(x => x.id !== uid));
      setSaveStatus(`✅ Deleted user ${email}`);
    } catch (err) {
      setSaveStatus('Error: ' + err.message);
    }
  }

  async function handleSetPassword() {
    if (!newPassword || newPassword.length < 6) {
      setSaveStatus('Password must be at least 6 characters'); return;
    }
    try {
      await api.adminSetPassword(setPasswordUser.uid, newPassword);
      setSaveStatus(`✅ Password updated for ${setPasswordUser.email}`);
      setSetPasswordUser(null);
      setNewPassword('');
    } catch (err) {
      setSaveStatus('Error: ' + err.message);
    }
  }

  async function handleResetPassword(email, displayName) {
    if (!email) { alert('No email address for this user'); return; }
    try {
      await sendPasswordResetEmail(auth, email);
      setSaveStatus(`✅ Password reset email sent to ${email}`);
    } catch (err) {
      setSaveStatus('Error: ' + err.message);
    }
  }

  function handleTabChange(tab) {
    setEditingBracket(null);
    setEditingGame(null);
    setError('');
    setActiveTab(tab);
  }

  return (
    <>
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 className="page-title">ADMIN <span>PANEL</span></h1>
          <p className="page-subtitle">Pool management & controls</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={async () => {
            try {
              const [gamesData, teamsData, wiringData, brandingData] = await Promise.all([
                api.adminGetGames(),
                api.getTeams(),
                fetch(`${process.env.REACT_APP_API_URL || '/api'}/wiring`).then(r => r.json()).catch(() => null),
                fetch(`${process.env.REACT_APP_API_URL || '/api'}/branding`).then(r => r.json()).catch(() => ({})),
              ]);
              const teamsById = {};
              (teamsData?.teams || []).forEach(t => { teamsById[t.id] = t; });
              setResultsBracketData({ games: Array.isArray(gamesData) ? gamesData : [], teamsById, wiring: wiringData, branding: brandingData });
              setShowResultsBracket(true);
            } catch (err) { msg(err.message, true); }
          }}>🏆 Results Bracket</button>
          <button className="btn btn-secondary" onClick={handlePoll}>🔄 Poll ESPN</button>
          <button className="btn btn-primary" onClick={handleRecalculate}>⚡ Recalculate Scores</button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {actionMsg && <div className="alert alert-success">{actionMsg}</div>}

      <div className="tabs">
        {TABS.map(t => (
          <button key={t} className={`tab${activeTab === t ? ' active' : ''}`} onClick={() => handleTabChange(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-container"><div className="spinner"></div></div>
      ) : (
        <>
          {/* OVERVIEW */}
          {activeTab === 'overview' && stats && (
            <div>
              <div className="grid-4" style={{ marginBottom: 24 }}>
                <div className="stat-box">
                  <div className="stat-value">{stats.totalBrackets}</div>
                  <div className="stat-label">Total Brackets</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">{stats.totalUsers}</div>
                  <div className="stat-label">Users</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">{stats.gamesCompleted}/{stats.gamesTotal}</div>
                  <div className="stat-label">Games Completed</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">{stats.scores?.max || 0}</div>
                  <div className="stat-label">High Score</div>
                </div>
              </div>
              <div className="grid-3">
                <div className="stat-box">
                  <div className="stat-value">{stats.scores?.avg || 0}</div>
                  <div className="stat-label">Avg Score</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">{stats.scores?.min || 0}</div>
                  <div className="stat-label">Low Score</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">
                    {Object.keys(stats.sleeperPopularity || {}).length === 0 ? '—' :
                      (id => teamsById[id]?.name || id)(
                        Object.entries(stats.sleeperPopularity).sort((a,b) => b[1]-a[1])[0]?.[0]
                      )}
                  </div>
                  <div className="stat-label">Most Popular Sleeper</div>
                </div>
              </div>
              {Object.keys(stats.sleeperPopularity || {}).length > 0 && (
                <div className="card" style={{ marginTop: 24 }}>
                  <div className="card-header"><h2 className="card-title">SLEEPER POPULARITY</h2></div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {Object.entries(stats.sleeperPopularity)
                      .sort((a,b) => b[1]-a[1])
                      .map(([teamId, count]) => (
                        <div key={teamId} style={{ background: 'var(--navy-light)', border: '1px solid var(--slate)', borderRadius: 8, padding: '10px 16px', textAlign: 'center' }}>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'var(--gold)' }}>{count}</div>
                          <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gray)' }}>{teamsById[teamId]?.name || teamId}</div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
              {/* Announcement Banner Admin Control */}
              <div className="card" style={{ marginTop: 24 }}>
                <div className="card-header">
                  <h2 className="card-title">ANNOUNCEMENT BANNER</h2>
                  <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.82rem', color: 'var(--gray)' }}>
                    Shown to all users on their Dashboard when non-empty
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ margin: 0, flex: 1 }}>
                    <label className="form-label">Message (leave blank to hide)</label>
                    <input className="form-input" value={announcementDraft}
                      onChange={e => setAnnouncementDraft(e.target.value)}
                      placeholder="e.g. Final Four picks lock Thursday at noon!" />
                  </div>
                  <button className="btn btn-primary" disabled={announcementSaving}
                    onClick={async () => {
                      setAnnouncementSaving(true);
                      try {
                        const apiBase = process.env.REACT_APP_API_URL || '/api';
                        const token = await (async () => { try { const { getAuth } = await import('firebase/auth'); return await getAuth().currentUser?.getIdToken(); } catch { return null; } })();
                        await fetch(`${apiBase}/admin/announcement`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                          body: JSON.stringify({ text: announcementDraft }),
                        });
                        setAnnouncement(announcementDraft);
                        msg(announcementDraft ? '✓ Announcement posted' : '✓ Announcement cleared');
                      } catch (e) { msg(e.message, true); }
                      finally { setAnnouncementSaving(false); }
                    }}>
                    {announcementSaving ? 'Saving...' : announcementDraft ? 'Post Announcement' : 'Clear Announcement'}
                  </button>
                </div>
                {announcement && (
                  <div className="alert alert-info" style={{ marginTop: 12 }}>
                    <strong>Currently showing:</strong> {announcement}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* BRACKETS */}
          {activeTab === 'brackets' && editingBracket && (
            <AdminBracketEditPage
              bracketId={editingBracket}
              onClose={() => { setEditingBracket(null); loadData('brackets'); }}
              wiring={wiring}
              playInRoundName={playInRoundName}
            />
          )}
          {activeTab === 'brackets' && !editingBracket && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">ALL BRACKETS ({brackets.length})</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                  {Object.keys(paidOverrides).length > 0 && (
                    <button className="btn btn-primary btn-sm" onClick={async () => {
                      try {
                        await Promise.all(Object.entries(paidOverrides).map(([id, paid]) =>
                          api.adminSetBracketPaid(id, paid)
                        ));
                        msg(`✓ Paid status saved for ${Object.keys(paidOverrides).length} bracket${Object.keys(paidOverrides).length !== 1 ? 's' : ''}`);
                        setPaidOverrides({});
                        loadData('brackets');
                      } catch (err) { msg(err.message, true); }
                    }}>
                      ★ Save Paid Changes ({Object.keys(paidOverrides).length})
                    </button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => {
                    const rows = [
                      ['bracketId','bracketName','userName','userEmail','score','tiebreaker','sleeperTeamId','championPick','paid','paidAt','submittedAt','picks','playInPicks'].join(',')
                    ];
                    brackets.forEach(b => {
                      const champId = b.picks?.['final-1'] || '';
                      rows.push([
                        b.id,
                        JSON.stringify(b.name || ''),
                        JSON.stringify(b.userName || ''),
                        JSON.stringify(b.userEmail || ''),
                        b.score || 0,
                        b.tiebreaker || '',
                        b.sleeperTeamId || '',
                        champId,
                        b.paid ? 'true' : 'false',
                        b.paidAt || '',
                        b.submittedAt || '',
                        JSON.stringify(JSON.stringify(b.picks || {})),
                        JSON.stringify(JSON.stringify(b.playInPicks || {})),
                      ].join(','));
                    });
                    const csv = rows.join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `mm-pool-brackets-${new Date().toISOString().slice(0,10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}>⬇ Export CSV</button>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <BracketsTable
                  brackets={brackets}
                  bracketSort={bracketSort}
                  setBracketSort={setBracketSort}
                  teamsById={teamsById}
                  setEditingBracket={setEditingBracket}
                  handleDeleteBracket={handleDeleteBracket}
                  onPrint={openPrint}
                  api={api}
                  msg={msg}
                  loadBrackets={() => loadData('brackets')}
                  eliminatedIds={buildEliminatedSet(games, espnSlugToId)}
                  gamesById={Object.fromEntries((games||[]).map(g => [g.id, g]))}
                  paidOverrides={paidOverrides}
                  espnNameCache={espnNameCache}
                  onTogglePaid={(id, currentPaid) => setPaidOverrides(prev => {
                    const next = { ...prev };
                    // If toggling back to original, remove from overrides
                    const originalPaid = !!brackets.find(b => b.id === id)?.paid;
                    const newPaid = !currentPaid;
                    if (newPaid === originalPaid) delete next[id];
                    else next[id] = newPaid;
                    return next;
                  })}
                />
              </div>
            </div>
          )}

          {/* GAMES */}
          {activeTab === 'games' && (() => {
            const ROUND_ORDER = { playin:0, R64:1, R32:2, R16:3, R8:4, R4:5, Final:6 };
            const REGION_ORDER = { East:0, South:1, Midwest:2, West:3 };
            const filtered = (gameRoundFilter ? games.filter(g => g.round === gameRoundFilter) : games).filter(g => !g.isOther && g.round !== 'other');
            const filteredGames = [...filtered].sort((a, b) => {
              // Sort by round first
              const rDiff = (ROUND_ORDER[a.round] ?? 9) - (ROUND_ORDER[b.round] ?? 9);
              if (rDiff !== 0) return rDiff;
              // Then by region
              const aDiff = (REGION_ORDER[a.region] ?? 9) - (REGION_ORDER[b.region] ?? 9);
              if (aDiff !== 0) return aDiff;
              // Then by game ID number
              return a.id.localeCompare(b.id);
            });
            return (
            <div>
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">GAMES</h2>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button className="btn btn-secondary btn-sm" disabled={backupBusy}
                      onClick={async () => {
                        setBackupBusy(true); setBackupMsg('');
                        try {
                          const r = await api.adminBackupGames();
                          setBackupMeta({ exists: true, backedUpAt: r.backedUpAt, gameCount: r.gameCount });
                          setBackupMsg(`✅ Backed up ${r.gameCount} games`);
                        } catch(e) { setBackupMsg('❌ ' + e.message); }
                        finally { setBackupBusy(false); }
                      }}>
                      💾 {backupBusy ? 'Saving...' : 'Backup Games'}
                    </button>
                    <button className="btn btn-secondary btn-sm" disabled={backupBusy || !backupMeta?.exists}
                      style={{ borderColor: 'var(--orange)', color: 'var(--orange)' }}
                      onClick={async () => {
                        if (!backupMeta?.exists) return;
                        const when = new Date(backupMeta.backedUpAt).toLocaleString();
                        if (!window.confirm(`Restore ${backupMeta.gameCount} games from backup taken ${when}?\n\nThis will overwrite ALL current game data.`)) return;
                        setBackupBusy(true); setBackupMsg('');
                        try {
                          const r = await api.adminRestoreGames();
                          setBackupMsg(`✅ Restored ${r.gameCount} games — recalculating scores...`);
                          setGames(await api.adminGetGames());
                        } catch(e) { setBackupMsg('❌ ' + e.message); }
                        finally { setBackupBusy(false); }
                      }}>
                      ↩ Restore{backupMeta?.exists ? ` (${new Date(backupMeta.backedUpAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })})` : ''}
                    </button>
                    <select className="form-select" style={{ width: 'auto', padding: '4px 8px', fontSize: '0.85rem' }}
                      value={gameRoundFilter} onChange={e => setGameRoundFilter(e.target.value)}>
                      <option value="">All Rounds</option>
                      <option value="playin">First Four (Play-In)</option>
                      <option value="R64">Round of 64</option>
                      <option value="R32">Round of 32</option>
                      <option value="R16">Sweet 16</option>
                      <option value="R8">Elite Eight</option>
                      <option value="R4">Final Four</option>
                      <option value="Final">Championship</option>
                    </select>
                    <span style={{ fontSize: '0.85rem', color: 'var(--gray)' }}>
                      {filteredGames.length} game{filteredGames.length !== 1 ? 's' : ''} · click Edit to update result
                    </span>
                  </div>
                </div>
                {backupMsg && (
                  <div className={`alert ${backupMsg.startsWith('✅') ? 'alert-success' : 'alert-error'}`} style={{ margin: '0 0 12px 0' }}>
                    {backupMsg}
                  </div>
                )}
                <div style={{ overflowX: 'auto' }}>
                  <table className="leaderboard-table">
                    <thead>
                      <tr>
                        <th>Game</th>
                        <th>Round</th>
                        <th>Status</th>
                        <th>Score</th>
                        <th>Winner</th>
                        <th>Updated</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredGames.map((g, gIdx) => {
                        const isEditing = editingGame === g.id;
                        const name = t => teamsById[t?.id]?.name || t?.name || t?.abbrev || t?.id || '?';
                        const isTbd = t => !t || t.id === 'tbd' || t.id?.includes('winner');
                        const gameLabel = (isTbd(g.homeTeam) && isTbd(g.awayTeam))
                          ? (g.shortName || g.id)
                          : `(${g.homeTeam?.seed||'?'}) ${name(g.homeTeam)} vs (${g.awayTeam?.seed||'?'}) ${name(g.awayTeam)}`;
                        const openEdit = (game) => {
                          setEditingGame(game.id);
                          setGameEdit({ status: game.status || 'scheduled', homeScore: game.homeTeam?.score ?? '', awayScore: game.awayTeam?.score ?? '', winner: game.winner || '' });
                        };
                        return (
                          <React.Fragment key={g.id}>
                            <tr style={{ background: isEditing ? 'rgba(75,139,190,0.12)' : 'transparent' }}>
                              <td style={{ fontSize: '0.9rem' }}>{gameLabel}</td>
                              <td><span className="badge badge-orange">{g.round}</span></td>
                              <td>
                                {g.status === 'live' && <span className="badge badge-green">● LIVE</span>}
                                {g.status === 'final' && <span className="badge badge-gray">FINAL</span>}
                                {g.status === 'scheduled' && <span className="badge badge-orange">Scheduled</span>}
                                {g.manualOverride && <span style={{ fontSize: '0.7rem', color: 'var(--orange)', marginLeft: 4 }}>✎</span>}
                              </td>
                              <td style={{ fontFamily: 'var(--font-condensed)' }}>
                                {g.homeTeam?.abbrev || '?'} {g.homeTeam?.score ?? '—'} · {g.awayTeam?.abbrev || '?'} {g.awayTeam?.score ?? '—'}
                              </td>
                              <td style={{ fontSize: '0.8rem', color: g.winner ? 'var(--green)' : 'var(--gray)' }}>
                                {g.winner ? (teamsById[g.winner]?.name || g.winner) : '—'}
                              </td>
                              <td style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>
                                {g.lastUpdated ? new Date(g.lastUpdated).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                              </td>
                              <td>
                                {isEditing
                                  ? <button className="btn btn-secondary btn-sm" onClick={() => setEditingGame(null)}>✕ Close</button>
                                  : <button className="btn btn-secondary btn-sm" onClick={() => openEdit(g)}>Edit</button>}
                              </td>
                            </tr>
                            {isEditing && (
                              <InlineGameEdit
                                game={g}
                                gameEdit={gameEdit}
                                setGameEdit={setGameEdit}
                                onSave={handleSaveGame}
                                onCancel={() => setEditingGame(null)}
                                hasPrev={gIdx > 0}
                                hasNext={gIdx < filteredGames.length - 1}
                                onPrev={() => openEdit(filteredGames[gIdx - 1])}
                                onNext={() => openEdit(filteredGames[gIdx + 1])}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ); })()}

          {/* RESULTS BRACKET */}
          {activeTab === 'results' && (
            <div className="card" style={{ padding: 20 }}>
              <div className="card-header" style={{ marginBottom: 16 }}>
                <h2 className="card-title">RESULTS BRACKET</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="btn btn-secondary btn-sm" disabled={backupBusy}
                    onClick={async () => {
                      setBackupBusy(true); setBackupMsg('');
                      try {
                        const r = await api.adminBackupGames();
                        setBackupMeta({ exists: true, backedUpAt: r.backedUpAt, gameCount: r.gameCount });
                        setBackupMsg(`✅ Backed up ${r.gameCount} games`);
                      } catch(e) { setBackupMsg('❌ ' + e.message); }
                      finally { setBackupBusy(false); }
                    }}>
                    💾 {backupBusy ? 'Saving...' : 'Backup Games'}
                  </button>
                  <button className="btn btn-secondary btn-sm" disabled={backupBusy || !backupMeta?.exists}
                    style={{ borderColor: 'var(--orange)', color: 'var(--orange)' }}
                    onClick={async () => {
                      if (!backupMeta?.exists) return;
                      const when = new Date(backupMeta.backedUpAt).toLocaleString();
                      if (!window.confirm(`Restore ${backupMeta.gameCount} games from backup taken ${when}?\n\nThis will overwrite ALL current game data.`)) return;
                      setBackupBusy(true); setBackupMsg('');
                      try {
                        const r = await api.adminRestoreGames();
                        setBackupMsg(`✅ Restored ${r.gameCount} games`);
                      } catch(e) { setBackupMsg('❌ ' + e.message); }
                      finally { setBackupBusy(false); }
                    }}>
                    ↩ Restore{backupMeta?.exists ? ` (${new Date(backupMeta.backedUpAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })})` : ''}
                  </button>
                  <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gray)' }}>
                    Click a team to mark them as winner · Auto-saves instantly
                  </span>
                </div>
              </div>
              {backupMsg && (
                <div className={`alert ${backupMsg.startsWith('✅') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 12 }}>
                  {backupMsg}
                </div>
              )}
              <AdminResultsBracket wiring={wiring} playInRoundName={playInRoundName} />
            </div>
          )}

          {/* USERS */}
          {activeTab === 'users' && setPasswordUser && (
            <div className="card" style={{ marginBottom: 16, border: '2px solid var(--blue)' }}>
              <div className="card-header">
                <h3 className="card-title" style={{ fontSize: '1.1rem' }}>SET PASSWORD: {setPasswordUser.email}</h3>
                <button className="btn btn-secondary btn-sm" onClick={() => setSetPasswordUser(null)}>Cancel</button>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <div className="form-group" style={{ margin: 0, flex: 1 }}>
                  <label className="form-label">New Password (min 6 characters)</label>
                  <input className="form-input" type="text" value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    autoFocus />
                </div>
                <button className="btn btn-primary" onClick={handleSetPassword}>
                  💾 Set Password
                </button>
              </div>
              <p style={{ marginTop: 10, fontFamily: 'var(--font-condensed)', fontSize: '0.82rem', color: 'var(--gray)' }}>
                Tell the user their new password directly — they can change it after logging in.
              </p>
            </div>
          )}
          {activeTab === 'users' && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">USERS ({users.length})</h2>
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  const year = new Date().getFullYear();
                  const csv = ['Name,Email,Admin']
                    .concat(users.map(u => `"${u.displayName || ''}","${u.email || ''}","${u.isAdmin ? 'Yes' : 'No'}"`))
                    .join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `mm-pool-users-${year}.csv`; a.click();
                  URL.revokeObjectURL(url);
                }}>⬇ Export Emails CSV</button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="leaderboard-table">
                  <thead>
                    <tr>
                      {[['displayName','Name'],['email','Email'],['isAdmin','Admin'],['createdAt','Joined']].map(([col,label]) => (
                        <th key={col} onClick={() => setUserSort(s => ({ col, dir: s.col===col && s.dir==='asc' ? 'desc' : 'asc' }))}
                          style={{ cursor: 'pointer' }}>
                          {label} <span style={{ fontSize: '0.6rem', opacity: userSort.col===col ? 1 : 0.3 }}>{userSort.col===col ? (userSort.dir==='asc' ? '↑' : '↓') : '↕'}</span>
                        </th>
                      ))}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...users].sort((a,b) => {
                        const d = userSort.dir === 'asc' ? 1 : -1;
                        if (userSort.col === 'isAdmin') return d * ((a.isAdmin?1:0)-(b.isAdmin?1:0));
                        return d * (a[userSort.col]||'').localeCompare(b[userSort.col]||'');
                      }).map(u => (
                      <tr key={u.id}>
                        <td><strong>{u.displayName}</strong></td>
                        <td>{u.email}</td>
                        <td>
                          {u.isAdmin
                            ? <span className="badge badge-orange">Admin</span>
                            : <span className="badge badge-gray">User</span>
                          }
                        </td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--gray)' }}>
                          {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className={`btn btn-sm ${u.isAdmin ? 'btn-danger' : 'btn-secondary'}`}
                              onClick={() => handleToggleAdmin(u.id, u.isAdmin, u.email)}
                            >
                              {u.isAdmin ? 'Revoke Admin' : 'Make Admin'}
                            </button>
                            <button
                              className={`btn btn-sm ${u.isObserver ? 'btn-primary' : 'btn-secondary'}`}
                              title={u.isObserver ? 'Observer: can see all pages without submitting a bracket' : 'Grant Observer access'}
                              onClick={async () => {
                                try {
                                  await api.adminUpdateUser(u.id, { isObserver: !u.isObserver });
                                  setUsers(users => users.map(x => x.id === u.id ? { ...x, isObserver: !u.isObserver } : x));
                                  msg(`✓ Observer ${!u.isObserver ? 'granted' : 'revoked'} for ${u.email}`);
                                } catch (err) { msg(err.message, true); }
                              }}
                            >
                              {u.isObserver ? '👁 Observer' : 'Observer'}
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleResetPassword(u.email, u.displayName)}
                              title="Send password reset email"
                            >
                              Email Reset
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => { setSetPasswordUser({ uid: u.id, email: u.email, displayName: u.displayName }); setNewPassword(''); }}
                              title="Set password directly"
                            >
                              Set PW
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteUser(u.id, u.email)}
                              title="Delete user"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SETUP */}
          {activeTab === 'setup' && <AdminSetupPage />}

          {/* ARCHIVE */}
          {activeTab === 'archive' && <AdminArchivePage />}

          {/* POLLER */}
          {activeTab === 'poller' && <AdminPollerPage />}

          {/* BRANDING */}
          {activeTab === 'scoring' && scoringConfig && (() => {
            const ROUND_LABELS = { playin: 'Play-In', R64: 'Round of 64', R32: 'Round of 32', R16: 'Sweet 16', R8: 'Elite Eight', R4: 'Final Four', Final: 'Championship' };
            const rp = scoringConfig.roundPoints || {};
            const updateRp = (round, val) => setScoringConfig(c => ({ ...c, roundPoints: { ...c.roundPoints, [round]: Number(val) } }));
            const save = async () => {
              setScoringMsg('Saving...');
              try {
                await api.put('/admin/scoring', scoringConfig);
                setScoringMsg('✅ Saved & scores recalculated!');
              } catch (e) { setScoringMsg('❌ ' + e.message); }
              setTimeout(() => setScoringMsg(''), 4000);
            };
            return (
              <div className="card" style={{ maxWidth: 520 }}>
                <div className="card-header"><h2 className="card-title">SCORING SETTINGS</h2></div>
                <div style={{ padding: '20px 24px' }}>
                  <h3 style={{ fontFamily: 'var(--font-condensed)', fontSize: '1rem', color: 'var(--blue)', letterSpacing: '1px', marginBottom: 12 }}>POINTS PER ROUND</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '0.8rem', color: 'var(--gray)' }}>Round</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', fontSize: '0.8rem', color: 'var(--gray)' }}>Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(ROUND_LABELS).map(([round, label]) => (
                        <tr key={round} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={{ padding: '8px', fontFamily: 'var(--font-condensed)', fontWeight: 700 }}>{label}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <input type="number" min="0" max="99" value={rp[round] ?? ''} onChange={e => updateRp(round, e.target.value)}
                              style={{ width: 64, textAlign: 'center', padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem' }} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <h3 style={{ fontFamily: 'var(--font-condensed)', fontSize: '1rem', color: 'var(--blue)', letterSpacing: '1px', marginBottom: 12 }}>PLAY-IN SWEEP BONUS</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                    <input type="number" min="0" max="99" value={scoringConfig.playInBonus ?? 1}
                      onChange={e => setScoringConfig(c => ({ ...c, playInBonus: Number(e.target.value) }))}
                      style={{ width: 64, textAlign: 'center', padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem' }} />
                    <span style={{ fontFamily: 'var(--font-condensed)', color: 'var(--gray)' }}>bonus pts for getting all 4 First Four picks correct</span>
                  </div>

                  <h3 style={{ fontFamily: 'var(--font-condensed)', fontSize: '1rem', color: 'var(--blue)', letterSpacing: '1px', marginBottom: 12 }}>SLEEPER BONUS POINTS</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input type="radio" name="sleeperMode" value="match_round"
                        checked={scoringConfig.sleeperMode === 'match_round'}
                        onChange={() => setScoringConfig(c => ({ ...c, sleeperMode: 'match_round' }))} />
                      <span style={{ fontFamily: 'var(--font-condensed)' }}>Match round points (sleeper bonus = that round's point value)</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input type="radio" name="sleeperMode" value="custom"
                        checked={scoringConfig.sleeperMode === 'custom'}
                        onChange={() => setScoringConfig(c => ({ ...c, sleeperMode: 'custom' }))} />
                      <span style={{ fontFamily: 'var(--font-condensed)' }}>Custom amount:</span>
                      <input type="number" min="0" max="99" value={scoringConfig.sleeperCustomPts ?? 0}
                        disabled={scoringConfig.sleeperMode !== 'custom'}
                        onChange={e => setScoringConfig(c => ({ ...c, sleeperCustomPts: Number(e.target.value) }))}
                        style={{ width: 64, textAlign: 'center', padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem', opacity: scoringConfig.sleeperMode !== 'custom' ? 0.4 : 1 }} />
                      <span style={{ fontFamily: 'var(--font-condensed)', color: 'var(--gray)' }}>pts per sleeper win</span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button className="btn btn-primary" onClick={save}>Save &amp; Recalculate</button>
                    {scoringMsg && <span style={{ fontFamily: 'var(--font-condensed)', color: scoringMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{scoringMsg}</span>}
                  </div>
                  <p style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--gray)' }}>Saving will immediately recalculate all bracket scores with the new point values.</p>
                </div>
              </div>
            );
          })()}
          {activeTab === 'branding' && <AdminBrandingPage />}

          {/* DEADLINES */}}
          {activeTab === 'deadlines' && (
            <div className="card" style={{ maxWidth: 600 }}>
              <div className="card-header"><h2 className="card-title">TOURNAMENT DEADLINES</h2></div>
              <div className="alert alert-info" style={{ marginBottom: 20 }}>
                Brackets can only be submitted/edited before the Regular deadline.
                Play-In picks lock at the Play-In deadline.
              </div>
              <div className="form-group">
                <label className="form-label">Play-In Deadline (Tuesday before First Four)</label>
                <input
                  className="form-input"
                  type="datetime-local"
                  value={deadlines.playInDeadline}
                  onChange={e => setDeadlines(d => ({ ...d, playInDeadline: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Regular Bracket Deadline (Thursday before R64)</label>
                <input
                  className="form-input"
                  type="datetime-local"
                  value={deadlines.regularDeadline}
                  onChange={e => setDeadlines(d => ({ ...d, regularDeadline: e.target.value }))}
                />
              </div>
              <button className="btn btn-primary" onClick={handleSaveDeadlines}>
                Save Deadlines
              </button>
            </div>
          )}
        </>
      )}
    </div>

          {/* PAYOUT */}
          {activeTab === 'payment' && <AdminPaymentPage />}

          {activeTab === 'payout' && <AdminPayoutPage />}

      {/* Print loading overlay */}
      {printLoading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
          <div className="spinner" />
          <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '1rem', color: 'var(--cream)', letterSpacing: '1px' }}>Loading bracket for print...</div>
        </div>
      )}
      {printError && (
        <div className="alert alert-error" style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 999, minWidth: 300 }}>
          Print error: {printError}
        </div>
      )}
      {printData && (
        <BracketPrintView
          bracket={printData.bracket}
          games={printData.games}
          teamsById={printData.teamsById}
          wiring={printData.wiring}
          branding={printData.branding}
          espnNameCache={espnNameCache}
          scoringConfig={scoringConfig}
          playInRoundName={playInRoundName}
          onClose={closePrint}
        />
      )}
      {showResultsBracket && resultsBracketData && (
        <ResultsBracketPrintView
          games={resultsBracketData.games}
          teamsById={resultsBracketData.teamsById}
          wiring={resultsBracketData.wiring}
          branding={resultsBracketData.branding}
          playInRoundName={playInRoundName}
          onClose={() => setShowResultsBracket(false)}
        />
      )}
    </>
  );
}
